import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { PageHeader, Card, Input, Select } from "@/components/erp/ui";
import { TeamPrPanel } from "@/components/erp/purchasing/TeamPrPanel";
import { Users, Clock, UserCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { DesignControls } from "@/components/erp/DesignControls";
import { useDesignMe, pickDesignStatus } from "@/components/erp/DesignFloatBar";
import { DesignWeeklyChart, WorkEndSetting } from "@/components/erp/DesignWeekly";
import { KpiPoints } from "@/components/erp/DesignKpiPoints";
import { LiveChip, WoCard, PlanModal, TakeoverModal } from "@/components/erp/DesignWoCard";
import { statusMeta, fmtHM, WORK_KEYS, SPECIAL_KEYS, openDesignPip } from "@/lib/designTimer";
import { HelpPanel } from "@/components/erp/DesignExtras";
import { KonsultanPanel } from "@/components/erp/KonsultanPanel";

const PRI = { HIGH: 0, MEDIUM: 1, LOW: 2 };
const dl = (w) => (w.deadline ? String(w.deadline).slice(0, 10) : "9999");
const SORTS = {
  deadline: (a, b) => dl(a).localeCompare(dl(b)),
  priority: (a, b) => (b.express - a.express) || ((PRI[a.priority] ?? 1) - (PRI[b.priority] ?? 1)) || dl(a).localeCompare(dl(b)),
  newest: (a, b) => String(b.created_at).localeCompare(String(a.created_at)),
};

const Board = () => {
  const { user } = useAuth();
  const [d, setD] = useState({ wos: [], live: [] });
  const [meta, setMeta] = useState({ members: [], reasons: {}, can_plan: false });
  const [sort, setSort] = useState("priority");
  const [onlyMine, setOnlyMine] = useState(false);
  const [plan, setPlan] = useState(null);
  const [tk, setTk] = useState(null);
  const { session, elapsed, workToday, ctrl } = useDesignMe();
  const load = useCallback(() => { api.get("/design/board").then(({ data }) => setD(data)).catch(() => {}); }, []);
  useEffect(() => {
    load(); const t = setInterval(load, 30000);
    api.get("/design/members").then(({ data }) => setMeta(data)).catch(() => {});
    window.addEventListener("design-refresh", load);
    return () => { clearInterval(t); window.removeEventListener("design-refresh", load); };
  }, [load]);
  const wos = useMemo(() => [...d.wos].filter((w) => !onlyMine || w.mine).sort((a, b) => (b.mine - a.mine) || SORTS[sort](a, b)), [d.wos, sort, onlyMine]);
  const pick = (st, wo_id) => { const wid = wo_id || session?.wo_id; if (st !== "STOP" && !SPECIAL_KEYS.includes(st) && (wid || !WORK_KEYS.includes(st))) openDesignPip(); pickDesignStatus(st, wid); };
  return (
    <div className="space-y-5">
      <div className="p-4 rounded-xl bg-slate-900 border border-slate-800" data-testid="design-my-controls">
        <DesignControls session={session} elapsed={elapsed} workToday={workToday} onPick={(st) => pick(st)} idp="board" {...ctrl} />
        {!session && <p className="text-[11px] text-slate-400 mt-1">Pilih WO di bawah lalu klik &quot;Kerjakan WO ini&quot;. Setelah status diklik, jendela tombol kecil muncul selalu di atas aplikasi lain.</p>}
      </div>
      <Card className="p-4" data-testid="design-live-team">
        <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Users className="w-4 h-4" /> Tim Design sekarang</div>
        <div className="flex flex-wrap gap-1.5">{d.live.length ? d.live.map((s) => <span key={s.id} className="inline-flex items-center gap-1"><LiveChip s={s} /><span className="text-[10px] font-mono text-slate-400">{s.wo_code}</span></span>) : <span className="text-xs text-slate-400">Belum ada yang aktif</span>}</div>
      </Card>
      <div className="grid lg:grid-cols-2 gap-4"><HelpPanel /></div>
      <div className="flex flex-wrap items-center gap-2" data-testid="design-board-toolbar">
        <span className="text-xs text-slate-500">Urutkan:</span>
        <div className="w-full sm:w-80"><Select data-testid="design-sort" value={sort} onChange={(e) => setSort(e.target.value)}>
          <option value="priority">Prioritas kerja (Express → High → deadline)</option>
          <option value="deadline">Deadline terdekat</option>
          <option value="newest">WO terbaru</option>
        </Select></div>
        <label className="flex items-center gap-1.5 text-xs text-slate-600"><input type="checkbox" data-testid="design-only-mine" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} /> Hanya WO saya</label>
        <span className="ml-auto text-xs text-slate-400">WO yang Anda pegang selalu tampil paling atas</span>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="design-wo-list">
        {wos.map((w) => <WoCard key={w.id} w={w} me={user?.email} canPlan={meta.can_plan} active={session?.wo_id === w.id && WORK_KEYS.includes(session?.status)} onStart={(x) => pick("WORKING", x.id)} onPlan={setPlan} onTakeover={setTk} />)}
        {wos.length === 0 && <p className="text-sm text-slate-400">Tidak ada WO yang sedang di tahap Design.</p>}
      </div>
      <PlanModal w={plan} members={meta.members} onClose={() => setPlan(null)} />
      <TakeoverModal w={tk} me={user?.email} members={meta.members} reasons={meta.reasons} canPlan={meta.can_plan} onClose={() => setTk(null)} />
    </div>
  );
};

const Recap = () => {
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
  const [range, setRange] = useState({ start: today, end: today });
  const [who, setWho] = useState("");
  const [people, setPeople] = useState([]);
  const [d, setD] = useState({ rows: [] });
  useEffect(() => { api.get("/design/recap", { params: { ...range, email: who } }).then(({ data }) => setD(data)).catch(() => {}); }, [range, who]);
  useEffect(() => { if (d.manager && !people.length) { api.get("/design/members").then(({ data }) => { const ds = data.members.filter((m) => m.role === "designer" || (m.extra_roles || []).includes("designer")); setPeople(ds.length ? ds : data.members); }).catch(() => {}); } }, [d.manager]); // eslint-disable-line react-hooks/exhaustive-deps
  const cols = ["WORKING", "BRAINSTORMING", "HELP", "OFFICE", "ASSEMBLY", "LUNCH", "DINNER", "PRAY", "TOILET", "PAUSE", "VOID"];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2 items-end">
        <div><div className="text-xs text-slate-500">Dari</div><Input type="date" data-testid="recap-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} /></div>
        <div><div className="text-xs text-slate-500">Sampai</div><Input type="date" data-testid="recap-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} /></div>
        {d.manager ? (
          <div><div className="text-xs text-slate-500">Designer</div>
            <Select data-testid="recap-designer-select" value={who} onChange={(e) => setWho(e.target.value)}>
              <option value="">Semua designer</option>
              {[...people, ...d.rows.filter((r, i, a) => !people.some((p) => p.email === r.user_email) && a.findIndex((x) => x.user_email === r.user_email) === i).map((r) => ({ email: r.user_email, name: r.user_name }))].map((p) => <option key={p.email} value={p.email}>{p.name || p.email}</option>)}
            </Select>
          </div>
        ) : <span className="text-xs text-slate-400 pb-2" data-testid="recap-own-label">Menampilkan rekap Anda</span>}
        <span className="pb-2 basis-full sm:basis-auto sm:ml-auto"><WorkEndSetting manager={d.manager} /></span>
      </div>
      <DesignWeeklyChart email={who} />
      <KpiPoints start={range.start} end={range.end} email={who} />
      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="recap-table">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr>
            <th className="px-3 py-2 text-left">Tanggal</th><th className="px-3 py-2 text-left">Designer</th>
            {cols.map((c) => <th key={c} className="px-3 py-2 text-right">{statusMeta(c).label}</th>)}
            <th className="px-3 py-2 text-right">Jam Project</th><th className="px-3 py-2 text-right">Hadir</th><th className="px-3 py-2 text-right">KPI (8j)</th><th className="px-3 py-2 text-left">Per WO</th>
          </tr></thead>
          <tbody>
            {d.rows.map((r) => (
              <tr key={r.user_email + r.date} className="border-t border-slate-100" data-testid={`recap-row-${r.user_email}-${r.date}`}>
                <td className="px-3 py-2 font-mono text-xs">{r.date}</td><td className="px-3 py-2">{r.user_name}{r.overtime && <span className="ml-1 text-[10px] px-1.5 rounded bg-amber-100 text-amber-700 font-semibold" data-testid={`recap-overtime-${r.user_email}-${r.date}`}>LEMBUR</span>}</td>
                {cols.map((c) => <td key={c} className="px-3 py-2 text-right text-xs">{r.by_status[c] ? fmtHM(r.by_status[c]) : "—"}</td>)}
                <td className="px-3 py-2 text-right font-semibold">{fmtHM(r.work_seconds)}</td>
                <td className="px-3 py-2 text-right text-xs">{r.attendance_hours != null ? `${r.attendance_hours}j` : <span className="text-amber-600">belum clock in</span>}</td>
                <td className={`px-3 py-2 text-right font-semibold ${r.productive_pct >= 80 ? "text-emerald-600" : r.productive_pct >= 50 ? "text-amber-600" : "text-rose-600"}`} data-testid={`recap-kpi-${r.user_email}-${r.date}`}>{r.productive_pct}%</td>
                <td className="px-3 py-2 text-xs text-slate-500">{r.by_wo.map((x) => `${x.wo_code} ${fmtHM(x.seconds)}`).join(" · ") || "—"}</td>
              </tr>
            ))}
            {d.rows.length === 0 && <tr><td colSpan={16} className="px-3 py-6 text-center text-slate-400 text-sm">Belum ada data kerja di periode ini</td></tr>}
          </tbody>
        </table>
      </Card>
      <p className="text-[11px] text-slate-400">Jam Project = Working + Brainstorming + Help Team + Kegiatan Kantor + Assembly (kerja efektif). KPI = Jam Project ÷ 8 jam. Bonus per jam design bisa diatur di Payroll → KPI rule &quot;Per Jam Kerja Design&quot;.</p>
    </div>
  );
};

export default function DesignDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("board");
  const owner = user?.role === "owner" || user?.role === "admin";
  const kons = (user?.roles || []).includes("konsultan_3d");
  return (
    <div>
      <PageHeader title="Dashboard Design" subtitle={`Monitoring kerja tim design per WO · ${user?.name || ""}`} />
      <TeamPrPanel />
      {kons && <div className="mb-4 text-xs inline-flex px-3 py-1 rounded-full bg-sky-600 text-white" data-testid="kons-me-badge">Anda juga Konsultan 3D — terima tamu konsultasi lewat menu Booking</div>}
      <div className="flex flex-wrap gap-2 mb-5">
        {[["board", "Board WO Design", Clock], ["recap", "Rekap & KPI", Users], ...(owner ? [["kons", "Konsultan 3D", UserCheck]] : [])].map(([k, l, I]) => (
          <button key={k} data-testid={`design-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 rounded-full text-sm inline-flex items-center gap-1.5 border ${tab === k ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200"}`}><I className="w-4 h-4" />{l}</button>
        ))}
      </div>
      {tab === "board" ? <Board /> : tab === "kons" ? <KonsultanPanel /> : <Recap />}
    </div>
  );
}
